import axios, { AxiosRequestConfig } from 'axios';
import type { AssistantUsageInfo, AssistantPlanInfo, AssistantSource } from './assistantUsage';
import type { PlanLimitsInfo } from './planLimits';
import type { GesteId, IlotEnvoye } from '../types/metanote';
import { DEMANDER_LABELS } from '../utils/indexationLabels';

// ─── Types ────────────────────────────────────────────────────────────

/** Citation dans une réponse RAG */
export interface ChatCitation {
    sourceTitle: string;
    /** `document` (C19) : un extrait du texte même d'un document déposé */
    sourceType: 'note' | 'entree' | 'entreeShare' | 'perimetre' | 'thesaurus' | 'document';
    sourceId: string;
    sectionHeading?: string;
    score?: number;
    /**
     * Type de la note citée. Une citation issue d'une note `source` renvoie vers
     * un texte verbatim d'un document : l'interface le signale au lieu de la
     * présenter comme une note de l'utilisateur. Absent pour les entrées
     * thésauriques et les chunks antérieurs au backfill.
     */
    noteType?: 'manual' | 'source' | 'commentary';
    /** Fichier d'origine, pour une note `source` */
    sourceFileName?: string;
    /** Page dans ce fichier */
    sourcePage?: number;
    /** Extrait d'un document (C19) : le fichier à rouvrir, son type, la section d'un EPUB, la position estimée 1–99. */
    fileId?: string;
    fileType?: 'pdf' | 'epub' | 'html' | 'docx';
    sourceChapter?: number;
    sourceChapterLabel?: string;
    sourceParagraphEstimate?: number;
    /** Source d'une organisation (C26 SP4) : la citation vient de ses documents, travaux ou vocabulaire. */
    organisationId?: string;
    organisationNom?: string;
    /** Citation inline : index d'apparition dans la réponse (1..N). Absent = source probable (heuristique) ou message antérieur */
    index?: number;
    /** Passage du contexte réellement injecté (≤ 600 caractères) */
    excerpt?: string;
    /** Canal par lequel la source est entrée dans le prompt */
    origin?: 'search' | 'mention' | 'skos' | 'notesEntrees';
}

// ─── Types RAG Écriture (ActionProposal) ──────────────────────────────

export type ActionType = 'create' | 'update' | 'delete' | 'batch';
export type TargetType = 'entree' | 'note' | 'perimetre' | 'thesaurus';
export type ProposalStatus = 'pending' | 'accepted' | 'rejected' | 'executed' | 'error';

export interface ActionProposal {
    id: string;
    action: ActionType;
    targetType: TargetType;
    targetId?: string;
    targetLabel: string;
    summary: string;
    data: Record<string, any>;
    previousData?: Record<string, any>;
    reasoning?: string;
    status: ProposalStatus;
    /** ID de l'élément créé/modifié — persisté à l'exécution (le BulkJob pour une proposition `batch`) */
    resultId?: string;
    /** Message de résultat renvoyé par l'exécution (ex: « Lot créé : N élément(s) à examiner ») */
    resultMessage?: string;
    /** Nombre d'éléments résolus par une proposition `batch` — persisté et rejoué dans l'historique LLM */
    resultCount?: number;
}

export interface ActionExecutionResult {
    success: boolean;
    proposalId: string;
    action: ActionType;
    targetType: TargetType;
    resultId?: string;
    message: string;
    resultData?: any;
}

/** Pièce jointe traitée par le backend — y compris les fichiers écartés */
export interface ChatAttachment {
    fileName: string;
    mimeType: string;
    size?: number;
    /** 'native-document' | 'image-injected' | 'text-extracted' | 'skipped' */
    processingMode: string;
    /** Raison de l'écartement (processingMode 'skipped') */
    skipReason?: string;
}

/** Un extrait récupéré par la recherche, avec son sort réel dans le prompt */
export interface ContextReportChunk {
    sourceType: string;
    sourceId: string;
    label: string;
    score: number;
    /** false = récupéré mais exclu par le budget de troncature */
    included: boolean;
    /** Numéro de citation [n] dans le prompt — absent si exclu */
    ref?: number;
}

/** Ce qui a réellement été injecté dans le prompt (panneau « Contexte ») */
export interface ContextReport {
    chunks: { retrieved: number; included: number; items: ContextReportChunk[] };
    skos?: { concepts: { cle: string; entreeId?: string }[]; expandedTerms: number };
    mentions?: { type: string; label: string; value?: string; isDocument: boolean }[];
    history: { messages: number };
    notesEntrees?: number;
    /** Passage en cours de lecture injecté avec la question (lecteur mobile, C11 SP4 bis) */
    readingPassage?: { label: string; chars: number; truncated: boolean };
    /** « Demander sur ce document » (C19) : le document visé, nommé ; le mobile l'affiche sous la réponse. */
    documentScope?: { label: string };
    /**
     * Matière d'une partie du plateau (C28 SP3 bis) : ce que le message « Matière de la partie » a réellement
     * transmis au modèle, et ce que le budget ou les dix notes relues ont laissé dehors.
     */
    ilot?: { label: string; passagesIncluded: number; passagesOmitted: number; notesIncluded: number; notesOmitted: number; truncated: boolean };
}

/** Message dans une session de chat */
export interface ChatMessage {
    role: 'user' | 'assistant';
    content: string;
    citations?: ChatCitation[];
    actionProposals?: ActionProposal[];
    timestamp: string;
    tokensUsed?: number;
    /** Pièces jointes (messages user) */
    attachments?: ChatAttachment[];
    /** Rapport de contexte (messages assistant) */
    contextReport?: ContextReport;
    /** Statistiques de la génération (messages assistant, session courante) */
    stats?: ChatSendResponse['stats'];
}

/** Résumé d'une session (pour la liste) */
export interface ChatSessionSummary {
    id: string;
    title: string;
    messageCount: number;
    provider?: string;
    model?: string;
    updatedAt: string;
}

/** Session complète avec messages */
export interface ChatSession {
    _id: string;
    userId: string;
    title: string;
    messages: ChatMessage[];
    provider?: string;
    llmModel?: string;
    createdAt: string;
    updatedAt: string;
}

/** Mode de connaissance de l'assistant RAG */
export type KnowledgeMode = 'strict' | 'enriched' | 'free';

/** Paramètres pour envoyer un message */
export interface ChatSendParams {
    query: string;
    /** Texte d'affichage avec les @[Label] préservés (pour stockage en DB) */
    displayQuery?: string;
    sessionId?: string;
    searchOptions?: {
        perimetreIds?: string[];
        noteIds?: string[];
        /** « Demander sur ce document » (C19) : les extraits de ces documents sont cherchés en priorité */
        fileIds?: string[];
        /** La portée « organisation » de l'assistant personnel (C26 SP4) : le serveur compose les sources lisibles du membre. */
        organisationId?: string;
        sourceTypes?: string[];
        mode?: 'hybrid' | 'vector' | 'fulltext';
        top?: number;
        minScore?: number;
    };
    enableSkos?: boolean;
    skosDepth?: number;
    /** Mode de connaissance : strict (défaut), enriched, free */
    knowledgeMode?: KnowledgeMode;
    /** Items de contexte injectés par @mentions */
    contextItems?: { type: string; label: string; content: string; value?: string; fileUrl?: string; fileName?: string; mimeType?: string }[];
    /** Mode écriture RAG (propositions de création/modification/suppression) */
    writeMode?: boolean;
    /** Le cadre d'un plateau d'où part la question (C28 SP3 bis) : le serveur en relit la matière. */
    ilot?: IlotEnvoye;
    /** Un geste fixe sur ce cadre : seul l'identifiant voyage, la consigne vit au serveur. */
    geste?: GesteId;
    /** La question est posée dans une organisation (C26 SP2/SP3) : porte d'acteur au serveur, contingent de l'organisation, Strict. */
    organisationId?: string;
    /** Les sources cochées de la façade (C26 SP3) : le serveur compose ses passes ; `searchOptions` est alors ignoré. */
    sources?: { fileIds: string[]; noteIds: string[]; vocabulaire: boolean };
    /** Une production en forme fixe (C26 SP3) : l'identifiant seul, la consigne vit au serveur. */
    production?: { forme: 'resume' | 'faq' | 'fiche' | 'chronologie' | 'guide'; terme?: string };
}

/** Réponse du chat */
export interface ChatSendResponse {
    answer: string;
    citations: ChatCitation[];
    actionProposals?: ActionProposal[];
    /** Pièces jointes traitées (y compris écartées) — à afficher sur le message user */
    attachments?: ChatAttachment[];
    /** Rapport de contexte — à afficher sur le message assistant */
    contextReport?: ContextReport;
    sessionId: string;
    sessionTitle: string;
    provider: string;
    model: string;
    stats: {
        searchDurationMs: number;
        skosExpansionMs: number;
        llmDurationMs: number;
        totalDurationMs: number;
        chunksFound: number;
        tokensEstimated: number;
        citationsResolved: number;
        citationMarkersDropped: number;
        /** Provenance de la clé et consommation du mois (C18) */
        assistant?: { source: AssistantSource; plan: 'free' | 'plus'; usage?: AssistantUsageInfo };
    };
}

/** État de l'abonnement pour l'affichage des boutons (C18 SP2, D7) — jamais de nom de prestataire */
export interface BillingInfo {
    enabled: boolean;
    /** Peut passer au forfait Plus (forfait Découverte, pas posé à la main) */
    checkout: boolean;
    /** Peut gérer son abonnement (client connu) */
    portal: boolean;
}

/** Configuration LLM (masquée) — ou clé incluse et consommation (C18) */
export interface LLMConfigInfo {
    configured: boolean;
    /** `own` clé propre, `included` clé incluse, `null` rien ; absent sur une ancienne réponse */
    source?: AssistantSource | null;
    plan?: AssistantPlanInfo;
    /** Présent quand `source === 'included'` */
    usage?: AssistantUsageInfo;
    /** Abonnement (SP2) ; absent sur une ancienne réponse = aucun bouton */
    billing?: BillingInfo;
    /** Plafonds du forfait et jauges de fichiers (revue des coûts, 12/10/2026) ; absent sur une ancienne réponse */
    limits?: PlanLimitsInfo;
    provider?: string;
    model?: string;
    temperature?: number;
    maskedKey?: string;
    lastValidated?: string | null;
}

/** Paramètres pour sauvegarder la config LLM */
export interface LLMConfigSaveParams {
    provider: 'openai' | 'anthropic' | 'google' | 'mistral';
    apiKey: string;
    model?: string;
    temperature?: number;
}

/** Info d'un provider LLM */
export interface LLMProviderInfo {
    id: string;
    label: string;
    defaultModel: string;
    models: string[];
}

/** Modèle découvert dynamiquement via l'API du provider */
export interface DiscoveredModel {
    id: string;
    name: string;
    provider: string;
    capabilities?: string[];
    contextWindow?: number;
    createdAt?: string;
}

/** Résultat de la découverte dynamique des modèles */
export interface ModelDiscoveryResult {
    provider: string;
    models: DiscoveredModel[];
    fromCache: boolean;
    fetchedAt: string;
    fallbackModels: string[];
    defaultModel: string | null;
    error: string | null;
}

// ─── Erreurs typées de l'envoi ────────────────────────────────────────

/**
 * Code d'une erreur d'envoi (C11 SP4, C18). Le message reste celui affiché par le
 * bureau ; le code sert aux clients qui parlent autrement (assistant mobile).
 * `quota` = limite du fournisseur (429 nu) ; `quota_exceeded` = contingent
 * mensuel de la clé incluse atteint (429 + code QUOTA_EXCEEDED, C18).
 */
export type ChatSendErrorCode =
    | 'canceled' | 'timeout' | 'network' | 'session_auth'
    | 'organisation_absente' | 'not_configured' | 'llm_auth' | 'quota' | 'quota_exceeded' | 'llm_timeout' | 'provider' | 'server';

/** Codes serveur d'un refus du fournisseur (`api/src/llm/providerErrors.ts`, `refusalHttp`) */
const PROVIDER_REFUSAL_CODES = new Set(['LLM_BILLING', 'LLM_MODEL_UNAVAILABLE', 'LLM_REFUSED', 'LLM_UNAVAILABLE']);

export class ChatSendError extends Error {
    constructor(message: string, readonly code: ChatSendErrorCode, readonly usage?: AssistantUsageInfo) {
        super(message);
        this.name = 'ChatSendError';
    }
}

// ─── Config ───────────────────────────────────────────────────────────

const createConfig = (accessToken: string): AxiosRequestConfig => ({
    headers: {
        'Authorization': `Bearer ${accessToken}`,
        'AccessToken': accessToken,
        'Content-Type': 'application/json'
    }
});

// ─── Chat API ─────────────────────────────────────────────────────────

export const chatAPI = {
    /**
     * Envoie un message et reçoit une réponse RAG.
     *
     * Timeout : le backend borne chaque appel LLM à ~90 s avec un retry —
     * 240 s couvrent le pire cas au lieu de laisser l'interface gelée sans
     * limite. `signal` permet le bouton « Arrêter » (AbortController du thunk).
     */
    sendMessage: async (params: ChatSendParams, accessToken: string, signal?: AbortSignal): Promise<ChatSendResponse> => {
        try {
            const response = await axios.post('/api/chat', params, {
                ...createConfig(accessToken),
                timeout: 240_000,
                signal,
            });
            return response.data.data;
        } catch (error: any) {
            // Les messages sont ceux du bureau, inchangés ; le code (C11 SP4)
            // permet au mobile de choisir son propre libellé et son action.
            if (axios.isCancel(error) || error.code === 'ERR_CANCELED') {
                throw new ChatSendError('Génération interrompue — votre question a été restaurée.', 'canceled');
            }
            if (error.code === 'ECONNABORTED') {
                throw new ChatSendError('Le serveur n\'a pas répondu à temps. Votre question a été restaurée, réessayez.', 'timeout');
            }
            if (!error.response) {
                // Pas de réponse HTTP : le serveur est injoignable (réseau coupé, DNS, CORS)
                throw new ChatSendError('Erreur du service de chat', 'network');
            }
            if (error.response.data) {
                console.error('[chatAPI] Détails serveur:', JSON.stringify(error.response.data, null, 2));
            }
            const status = error.response.status;
            const serverCode = error.response.data?.code;
            const serverMessage: string | undefined = error.response.data?.message;
            if (status === 401) {
                // Le serveur renvoie 401 pour une clé LLM refusée par le fournisseur
                // (code LLM_AUTH_ERROR) comme pour une session expirée.
                if (serverCode === 'LLM_AUTH_ERROR') {
                    throw new ChatSendError(serverMessage || 'Erreur d\'authentification avec le fournisseur LLM. Vérifiez votre clé API.', 'llm_auth');
                }
                throw new ChatSendError('Authentification requise', 'session_auth');
            }
            // La portée « organisation » (C26 SP4) : la porte opaque, et elle seule (revue finale D4 : son corps
            // `Organisation non trouvée`), veut dire « vous n'en faites plus partie » ; tout autre 404 est ordinaire.
            if (status === 404 && params.searchOptions?.organisationId && serverMessage === 'Organisation non trouvée') {
                throw new ChatSendError(DEMANDER_LABELS.plusMembre, 'organisation_absente');
            }
            if (status === 422) {
                if (serverCode === 'LLM_NOT_CONFIGURED') {
                    throw new ChatSendError('Aucune clé API LLM configurée — ouvrez la configuration (bouton ⚙️) pour en ajouter une.', 'not_configured');
                }
                throw new ChatSendError(serverMessage || 'Configuration LLM requise', 'server');
            }
            if (status === 429) {
                if (serverCode === 'QUOTA_EXCEEDED') {
                    const data = error.response.data;
                    const usage = typeof data.limit === 'number' && typeof data.used === 'number' && typeof data.resetsOn === 'string'
                        ? { limit: data.limit, used: data.used, resetsOn: data.resetsOn }
                        : undefined;
                    throw new ChatSendError(serverMessage || 'Vous avez posé vos questions du mois.', 'quota_exceeded', usage);
                }
                // Débit limité par le fournisseur (`LLM_RATE_LIMITED`) : le serveur envoie
                // une phrase adaptée à la clé incluse ou propre ; repli historique sinon.
                throw new ChatSendError(serverMessage || 'Quota du fournisseur LLM atteint. Patientez un instant puis réessayez.', 'quota');
            }
            if (status === 504) {
                throw new ChatSendError('Le modèle a mis trop de temps à répondre. Réessayez, ou choisissez un modèle plus rapide.', 'llm_timeout');
            }
            // Refus du fournisseur (C18) : crédit épuisé, modèle indisponible, refus, panne.
            // La phrase du serveur est déjà humaine et adaptée à la provenance de la clé.
            if (serverCode && PROVIDER_REFUSAL_CODES.has(serverCode)) {
                throw new ChatSendError(serverMessage || 'Erreur du service de chat', 'provider');
            }
            throw new ChatSendError(serverMessage || 'Erreur du service de chat', 'server');
        }
    },

    /**
     * Liste les sessions de l'utilisateur
     */
    listSessions: async (accessToken: string, limit = 20, offset = 0, organisationId?: string): Promise<{
        sessions: ChatSessionSummary[];
        total: number;
    }> => {
        try {
            // Les conversations d'une façade d'organisation (C26 SP3) ; sans paramètre, celles d'ici.
            const organisation = organisationId ? `&organisationId=${encodeURIComponent(organisationId)}` : '';
            const response = await axios.get(`/api/chat/sessions?limit=${limit}&offset=${offset}${organisation}`, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de chargement des sessions');
        }
    },

    /**
     * Récupère une session complète
     */
    getSession: async (sessionId: string, accessToken: string): Promise<ChatSession> => {
        try {
            const response = await axios.get(`/api/chat/sessions/${sessionId}`, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            if (error.response?.status === 404) {
                throw new Error('Session non trouvée');
            }
            throw new Error(error.response?.data?.message || 'Erreur de chargement de la session');
        }
    },

    /**
     * Renomme une session
     */
    renameSession: async (sessionId: string, title: string, accessToken: string): Promise<void> => {
        try {
            await axios.put(`/api/chat/sessions/${sessionId}/rename`, { title }, createConfig(accessToken));
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de renommage');
        }
    },

    /**
     * Supprime une session
     */
    deleteSession: async (sessionId: string, accessToken: string): Promise<void> => {
        try {
            await axios.delete(`/api/chat/sessions/${sessionId}`, createConfig(accessToken));
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de suppression');
        }
    },
};

// ─── LLM Config API ───────────────────────────────────────────────────

export const llmConfigAPI = {
    /**
     * Récupère la configuration LLM (clé masquée)
     */
    getConfig: async (accessToken: string): Promise<LLMConfigInfo> => {
        try {
            const response = await axios.get('/api/llm-config', createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de chargement de la config LLM');
        }
    },

    /**
     * Sauvegarde la configuration LLM
     */
    saveConfig: async (params: LLMConfigSaveParams, accessToken: string): Promise<LLMConfigInfo> => {
        try {
            const response = await axios.put('/api/llm-config', params, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de sauvegarde');
        }
    },

    /**
     * Valide la clé API
     */
    validateConfig: async (accessToken: string): Promise<{ valid: boolean; error?: string }> => {
        try {
            const response = await axios.post('/api/llm-config/validate', {}, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de validation');
        }
    },

    /**
     * Supprime la configuration LLM
     */
    deleteConfig: async (accessToken: string): Promise<void> => {
        try {
            await axios.delete('/api/llm-config', createConfig(accessToken));
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de suppression');
        }
    },

    /**
     * Liste les providers supportés (liste statique/env — pas de clé requise)
     */
    getProviders: async (): Promise<LLMProviderInfo[]> => {
        try {
            const response = await axios.get('/api/llm-config/providers');
            return response.data.data;
        } catch (error: any) {
            throw new Error('Erreur de chargement des providers');
        }
    },

    /**
     * Récupère dynamiquement les modèles disponibles chez le provider
     * configuré par l'utilisateur (interroge l'API du provider avec sa clé).
     * 
     * @param accessToken - Token d'authentification
     * @param provider    - Forcer un provider (sinon celui configuré)
     * @param refresh     - Forcer le rafraîchissement du cache
     */
    getModels: async (
        accessToken: string,
        provider?: string,
        refresh?: boolean
    ): Promise<ModelDiscoveryResult> => {
        try {
            const params = new URLSearchParams();
            if (provider) params.set('provider', provider);
            if (refresh) params.set('refresh', 'true');
            const qs = params.toString();
            const url = `/api/llm-config/models${qs ? `?${qs}` : ''}`;
            const response = await axios.get(url, createConfig(accessToken));
            return response.data.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de chargement des modèles');
        }
    },
};

// ─── RAG Actions API (RAG Écriture) ──────────────────────────────────

export const ragActionsAPI = {
    /**
     * Exécute une proposition d'action validée par l'utilisateur
     */
    executeAction: async (
        sessionId: string,
        proposalId: string,
        accessToken: string
    ): Promise<ActionExecutionResult> => {
        try {
            const response = await axios.post(
                '/api/rag/execute-action',
                { sessionId, proposalId },
                createConfig(accessToken)
            );
            return response.data.data;
        } catch (error: any) {
            // 422 = échec MÉTIER structuré (sélection vide, périmètre introuvable…) :
            // le serveur renvoie un ActionExecutionResult complet avec son motif dans
            // `message`. Le remonter tel quel, sinon la carte perd le motif du refus
            // (le body de 422 n'a pas de `message` racine : il vit dans `data`).
            const result = error.response?.data?.data;
            if (error.response?.status === 422 && result && typeof result.message === 'string') {
                return result as ActionExecutionResult;
            }
            throw new Error(error.response?.data?.message || 'Erreur d\'exécution de l\'action');
        }
    },

    /**
     * Met à jour le statut d'une proposition (acceptée/rejetée)
     */
    updateProposalStatus: async (
        sessionId: string,
        proposalId: string,
        status: 'accepted' | 'rejected',
        accessToken: string
    ): Promise<void> => {
        try {
            await axios.post(
                '/api/rag/update-proposal-status',
                { sessionId, proposalId, status },
                createConfig(accessToken)
            );
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Erreur de mise à jour du statut');
        }
    },
};
